import { PatientClient, PatientDto, PatientIntakeDto } from '@medone/medonehp-api-client';

import { DuplicateMbiError, DuplicatePatientError } from '../../../../shared/common';
import { handleError } from '../../../../shared/common/HandleErrors';
import { getCleanMedicalSummaryFields, trimSpacesFromStrings } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { censusSlice } from './slice';

export type UpdatePatientOptions = {
    /**
     * SCRUM-1315 fail-closed gate, and the only field that gets one: the server reads any
     * non-null drugHistoryConsent as an opinion, so a value copied out of Redux can re-grant
     * a consent someone else just revoked and stamp the wrong recording user on the audit
     * row. Opt in ONLY where the user acted on the consent control itself.
     */
    includeDrugHistoryConsent?: boolean;
};

export function updatePatient(patient: PatientDto, options: UpdatePatientOptions = {}): AppThunk<Promise<boolean>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientClient(null, Axios);

        try {
            // trimSpacesFromStrings returns a fresh shallow copy, so the delete below cannot
            // reach the caller's Redux-derived object.
            const payload = trimSpacesFromStrings<PatientDto>(patient);

            if (!options.includeDrugHistoryConsent) {
                // delete, not `= undefined`: the key must not exist at all, because on the
                // wire a false is a revocation rather than "no opinion".
                delete payload.drugHistoryConsent;
            }

            const response = await client.post(payload);

            if (response.result.succeeded) {
                dispatch(censusSlice.actions.setPatient(response.result.entity));
            } else {
                if (response.result.message === 'Duplicate patient detected') {
                    throw new DuplicatePatientError(response.result.message, response.result.entity);
                } else if (response.result.message.startsWith('Duplicate MBI')) {
                    throw new DuplicateMbiError(response.result.message, response.result.entity);
                }

                handleError(response, () => true);
            }

            return response.result.succeeded;
        } catch (error) {
            // Ensure this gets bubbled up to the top level so we can handle on the UI
            if (error instanceof DuplicatePatientError || error instanceof DuplicateMbiError) {
                throw error;
            }

            handleError(error, () => true);
        }

        return false;
    };
}

export function syncLatestMedicalSummaryToPatientChart(): AppThunk<Promise<PatientIntakeDto>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const { currentCensus, currentPatientIntake } = getState().census;

        try {
            if (currentCensus != null && currentPatientIntake != null && currentCensus.patientIntakeId === currentPatientIntake.id) {
                const msValues = getCleanMedicalSummaryFields(currentPatientIntake?.medicalSummary);

                dispatch(censusSlice.actions.updateMedicalSummaryFields(msValues));
            }
        } catch (error) {
            handleError(error, () => true);
        }

        return null;
    };
}

export const selectPatient = (state: RootState) => state.census.patient;
